import React, { useEffect, useState, useCallback } from "react";
import { PlayCircle, Radio, TriangleAlert, Waves, Antenna, RefreshCcw } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const ALARM_CODES = [
  "low_battery", "valve_failure", "insufficient_balance", "overflow",
  "leakage", "reverse_flow", "signal_lost", "tamper",
];

export default function Simulator({ embedded = false }) {
  const [meters, setMeters] = useState([]);
  const [meterId, setMeterId] = useState("");
  const [delta, setDelta] = useState(0.05);
  const [count, setCount] = useState(24);
  const [step, setStep] = useState(30);
  const [alarmCode, setAlarmCode] = useState("low_battery");
  const [alarmSev, setAlarmSev] = useState("WARN");
  const [busy, setBusy] = useState(false);
  const [rawUplinks, setRawUplinks] = useState([]);
  const [rawLoading, setRawLoading] = useState(false);

  const loadRaw = useCallback(async () => {
    setRawLoading(true);
    try {
      const { data } = await api.get("/ingest/raw", { params: { limit: 20 } });
      setRawUplinks(data.data || []);
    } catch { /* silently ignore for now */ }
    setRawLoading(false);
  }, []);

  useEffect(() => {
    api.get("/meters", { params: { page_size: 200 } }).then(({ data }) => {
      setMeters(data.data || []);
      if (data.data?.[0]) setMeterId(data.data[0].id);
    });
    loadRaw();
    const t = setInterval(loadRaw, 8000);
    return () => clearInterval(t);
  }, [loadRaw]);

  const send = async (fn) => {
    if (!meterId) return toast.error("Select a meter first");
    setBusy(true);
    try { await fn(); } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const singleReading = () => send(async () => {
    const { data } = await api.post("/simulator/reading", { meter_id: meterId, delta_m3: Number(delta) });
    toast.success(`Reading ${data.reading_m3.toFixed(3)} m³ ingested`);
  });

  const bulkReadings = () => send(async () => {
    const { data } = await api.post("/simulator/auto", {
      meter_id: meterId, count: Number(count), step_minutes: Number(step),
    });
    toast.success(`Emitted ${data.emitted} uplinks`);
  });

  const raiseAlarm = () => send(async () => {
    await api.post("/simulator/alarm", { meter_id: meterId, code: alarmCode, severity: alarmSev });
    toast.success(`${alarmCode} alarm raised`);
  });

  const selected = meters.find((m) => m.id === meterId);

  return (
    <div className="space-y-6" data-testid="simulator-page">
      {!embedded && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-semibold">Developer tools</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight mt-1">Meter Simulator</h1>
        <p className="text-sm text-slate-500 mt-1">
          Drive synthetic uplinks and alarms to exercise the platform. In production this endpoint is fed by the LoRaWAN network server / NB‑IoT gateway.
        </p>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <Label className="text-xs uppercase tracking-wider text-slate-500">Target meter</Label>
        <Select value={meterId} onValueChange={setMeterId}>
          <SelectTrigger className="w-full mt-1 rounded-sm" data-testid="simulator-meter-select">
            <SelectValue placeholder="Select meter…" />
          </SelectTrigger>
          <SelectContent>
            {meters.map((m) => (
              <SelectItem key={m.id} value={m.id}>
                {(m.deveui || m.imei)} · {m.customer_name || "unassigned"} · {m.device_type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selected && (
          <div className="mt-3 text-xs text-slate-500 font-mono tabular-nums">
            Reading: {selected.reading_m3.toFixed(3)} m³ · Remaining: {selected.remaining_flow_m3.toFixed(3)} m³ · Valve: {selected.valve_state}
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Single reading */}
        <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="sim-single-card">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-8 w-8 rounded-sm bg-blue-50 flex items-center justify-center text-blue-600"><Radio size={14} /></div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Single uplink</div>
              <div className="font-heading font-semibold">Push one reading</div>
            </div>
          </div>
          <Label>Δ consumption (m³)</Label>
          <Input type="number" step="0.001" value={delta} onChange={(e) => setDelta(e.target.value)}
            data-testid="sim-delta-input" className="mt-1" />
          <Button onClick={singleReading} disabled={busy} data-testid="sim-single-submit"
            className="w-full mt-4 bg-blue-600 hover:bg-blue-700 rounded-sm">
            <PlayCircle size={14} className="mr-1" /> Send reading
          </Button>
        </div>

        {/* Auto backfill */}
        <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="sim-bulk-card">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-8 w-8 rounded-sm bg-indigo-50 flex items-center justify-center text-indigo-600"><Waves size={14} /></div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Backfill</div>
              <div className="font-heading font-semibold">Generate history</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Uplinks</Label>
              <Input type="number" value={count} onChange={(e) => setCount(e.target.value)}
                data-testid="sim-count-input" className="mt-1" />
            </div>
            <div>
              <Label>Step (min)</Label>
              <Input type="number" value={step} onChange={(e) => setStep(e.target.value)}
                data-testid="sim-step-input" className="mt-1" />
            </div>
          </div>
          <Button onClick={bulkReadings} disabled={busy} data-testid="sim-bulk-submit"
            className="w-full mt-4 bg-indigo-600 hover:bg-indigo-700 rounded-sm">
            <PlayCircle size={14} className="mr-1" /> Generate
          </Button>
        </div>

        {/* Alarm */}
        <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="sim-alarm-card">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-8 w-8 rounded-sm bg-red-50 flex items-center justify-center text-red-600"><TriangleAlert size={14} /></div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Alarm</div>
              <div className="font-heading font-semibold">Raise a fault</div>
            </div>
          </div>
          <Label>Code</Label>
          <Select value={alarmCode} onValueChange={setAlarmCode}>
            <SelectTrigger className="mt-1" data-testid="sim-alarm-code"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ALARM_CODES.map((c) => (<SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>))}
            </SelectContent>
          </Select>
          <Label className="mt-3 block">Severity</Label>
          <Select value={alarmSev} onValueChange={setAlarmSev}>
            <SelectTrigger className="mt-1" data-testid="sim-alarm-severity"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="INFO">INFO</SelectItem>
              <SelectItem value="WARN">WARN</SelectItem>
              <SelectItem value="CRITICAL">CRITICAL</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={raiseAlarm} disabled={busy} data-testid="sim-alarm-submit"
            className="w-full mt-4 bg-red-600 hover:bg-red-700 rounded-sm">
            <PlayCircle size={14} className="mr-1" /> Raise alarm
          </Button>
        </div>
      </div>

      {/* Recent raw UDP uplinks — captured by the bridge daemon */}
      <div className="bg-white border border-slate-200 rounded-sm" data-testid="raw-uplinks-card">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-sm bg-brand-50 flex items-center justify-center text-brand-600">
              <Antenna size={14} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Bridge capture</div>
              <div className="font-heading font-semibold">Recent raw UDP uplinks</div>
              <div className="text-xs text-slate-500 mt-0.5">Every datagram the UDP bridge forwards to <code className="font-mono">/api/ingest/raw</code>. Useful for reverse-engineering the meter frame.</div>
            </div>
          </div>
          <button onClick={loadRaw} className="text-xs text-slate-500 hover:text-brand-600 inline-flex items-center gap-1" data-testid="raw-uplinks-refresh">
            <RefreshCcw size={12} className={rawLoading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Received</th>
                <th className="text-left px-4 py-2 font-semibold">Source IP</th>
                <th className="text-right px-4 py-2 font-semibold">Bytes</th>
                <th className="text-left px-4 py-2 font-semibold">IMEI hint</th>
                <th className="text-left px-4 py-2 font-semibold">Bridge</th>
                <th className="text-left px-4 py-2 font-semibold">Hex (first 48 bytes)</th>
              </tr>
            </thead>
            <tbody data-testid="raw-uplinks-table-body">
              {rawUplinks.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-sm text-slate-400">
                  No raw uplinks yet — deploy the UDP bridge (see <code className="font-mono">/app/bridge/README.md</code>) and point a meter at it.
                </td></tr>
              )}
              {rawUplinks.map((u) => (
                <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2 font-mono text-xs">{new Date(u.received_at).toLocaleString()}</td>
                  <td className="px-4 py-2 font-mono text-xs">
                    {u.source_ip ? `${u.source_ip}${u.source_port ? `:${u.source_port}` : ""}` : "—"}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{u.byte_length}</td>
                  <td className="px-4 py-2 font-mono text-xs">{u.imei_hint || "—"}</td>
                  <td className="px-4 py-2 font-mono text-xs text-slate-500">{u.bridge_id || "—"}</td>
                  <td className="px-4 py-2 font-mono text-[11px] break-all max-w-md">
                    {u.hex_data?.slice(0, 96)}{u.hex_data?.length > 96 && "…"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
